// components/CommonTable.js
import React, { useMemo, useRef, useState } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import Pagination from "./Pagination";
import { Button } from "reactstrap";

const DOTS = "DOTS";

function range(start, end) {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => start + idx);
}

// helper: pagination range generator
function getPaginationRange({
  totalPageCount,
  currentPage,
  siblingCount = 1,
  boundaryCount = 1,
}) {
  const DOTS = "DOTS";
  const totalPageNumbers = siblingCount * 2 + boundaryCount * 2 + 3;

  if (totalPageCount <= totalPageNumbers) {
    return range(1, totalPageCount);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(
    currentPage + siblingCount,
    totalPageCount,
  );

  const showLeftDots = leftSiblingIndex > boundaryCount + 2;
  const showRightDots =
    rightSiblingIndex < totalPageCount - (boundaryCount + 1);

  const firstPages = range(1, boundaryCount);
  const lastPages = range(totalPageCount - boundaryCount + 1, totalPageCount);

  if (!showLeftDots && showRightDots) {
    const leftItemCount = boundaryCount + 1 + 2 * siblingCount;
    const leftRange = range(1, leftItemCount);
    return [...leftRange, DOTS, ...lastPages];
  }

  if (showLeftDots && !showRightDots) {
    const rightItemCount = boundaryCount + 1 + 2 * siblingCount;
    const rightRange = range(
      totalPageCount - rightItemCount + 1,
      totalPageCount,
    );
    return [...firstPages, DOTS, ...rightRange];
  }

  const middleRange = range(leftSiblingIndex, rightSiblingIndex);
  return [...firstPages, DOTS, ...middleRange, DOTS, ...lastPages];
}

const CommonTable = ({
  columns,
  data,
  defaultPageSize = 25,
  showPaginationTop = false,
  showPaginationBottom = true,
  className = "",
  rowClick,
  activePage,
  setPageNum,
  totalPages,
  pageNum,
  rowClassName,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    gotoPage,
    pageCount,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: defaultPageSize },
    },
    useSortBy,
    usePagination,
  );
  const tableWrapperRef = useRef(null);
  const totalMinWidth = columns.reduce(
    (sum, col) => sum + (col.minWidth || 100),
    0,
  );

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - tableWrapperRef.current.offsetLeft);
    setScrollLeft(tableWrapperRef.current.scrollLeft);
  };
  const onMouseLeave = () => setIsDragging(false);
  const onMouseUp = () => setIsDragging(false);
  const onMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - tableWrapperRef.current.offsetLeft;
    const walk = x - startX;
    tableWrapperRef.current.scrollLeft = scrollLeft - walk;
  };

  // pageIndex is 0-based; currentPage for pagination helper use 1-based
  // const currentPage = pageIndex + 1;
  // const paginationRange = useMemo(
  //   () =>
  //     getPaginationRange({
  //       totalPageCount: pageCount || 1,
  //       currentPage,
  //       siblingCount: 1, // you can increase siblings if you want more nearby pages
  //       boundaryCount: 1, // pages to always show at the start and end
  //     }),
  //   [pageCount, currentPage]
  // );

  // const renderPagination = () => {
  //   if (!pageCount || pageCount === 1) return null;

  //   return (
  //     <div className="d-flex justify-content-center align-items-center mt-2">
  //       {/* Previous icon/button */}
  //       <Button
  //         onClick={() => previousPage()}
  //         disabled={!canPreviousPage}
  //         size="sm"
  //         className="mr-2"
  //       >
  //         ‹ Prev
  //       </Button>

  //       {/* Page items */}
  //       {paginationRange.map((item, idx) =>
  //         item === DOTS ? (
  //           <span key={`dots-${idx}`} className="mx-1 px-2">
  //             …
  //           </span>
  //         ) : (
  //           <Button
  //             key={`page-${item}`}
  //             onClick={() => {
  //               // item is 1-based page number
  //               if (typeof item === "number") gotoPage(item - 1);
  //             }}
  //             size="sm"
  //             color={item === currentPage ? "info" : "secondary"}
  //             className="mx-1"
  //           >
  //             {item}
  //           </Button>
  //         )
  //       )}

  //       {/* Next icon/button */}
  //       <Button
  //         onClick={() => nextPage()}
  //         disabled={!canNextPage}
  //         size="sm"
  //         className="ml-2"
  //       >
  //         Next ›
  //       </Button>
  //     </div>
  //   );
  // };

  return (
    <>
      <div
        className="table-responsive"
        ref={tableWrapperRef}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onMouseDown={onMouseDown}
        onMouseLeave={onMouseLeave}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
      >
        {/* Top Pagination */}
        {/* {showPaginationTop && (
        <div className="d-flex justify-content-between mb-2">
          <Button
            onClick={() => previousPage()}
            disabled={!canPreviousPage}
            size="sm"
          >
            Prev
          </Button>
          <span>Page {pageIndex + 1}</span>
          <Button onClick={() => nextPage()} disabled={!canNextPage} size="sm">
            Next
          </Button>
        </div>
      )} */}
        {/* {showPaginationTop && renderPagination()} */}

        {/* Table */}
        <table
          {...getTableProps()}
          className={`table ${className}`}
          style={{ minWidth: totalMinWidth }}
        >
          <thead className="text-info">
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={column.id}
                    className={column.className || "align-top"}
                    style={{ cursor: column.canSort ? "pointer" : "default" }}
                  >
                    <div
                      className={`d-flex ${column.centerAlign
                          ? "justify-content-center align-items-center"
                          : column.rightAlign
                            ? "justify-content-end align-items-center"
                            : "align-items-center"
                        }`}
                    >
                      {column.render("Header")}
                      {/* Add sort indicator */}
                      <div className="ml-1">
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <i className="fa fa-sort-down" />
                          ) : (
                            <i className="fa fa-sort-up" />
                          )
                        ) : column.canSort ? (
                          <i className="fa fa-sort fa-xs text-light" />
                        ) : null}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr
                  {...row.getRowProps()}
                  key={row.id}
                  className={rowClassName ? rowClassName(row.original) : ""}
                  onClick={rowClick ? () => rowClick(row.original) : undefined}
                  style={{
                    cursor: rowClick ? "pointer" : "default",
                  }}
                >
                  {row.cells.map((cell) => (
                    <td
                      {...cell.getCellProps()}
                      key={cell.column.id}
                      className={cell.column.className || ""}
                      onClick={() => {
                        if (cell.column.onClick) {
                          cell.column.onClick(cell, row);
                        }
                      }}
                      style={{
                        cursor:
                          rowClick || cell.column.onClick
                            ? "pointer"
                            : "default",
                      }}
                    >
                      {cell.render("Cell")}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Bottom Pagination */}
      {showPaginationBottom && (
        <Pagination
          totalPages={totalPages || pageOptions.length}
          activePage={activePage || pageIndex + 1}
          setPageNum={setPageNum || ((page) => gotoPage(page - 1))}
        />
      )}
      {/* {showPaginationBottom && renderPagination()} */}
    </>
  );
};

export default CommonTable;
